import { ITERABLE_API_DOCS_URL } from 'constants/urls';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { NavDropdownGroupProps, NavigationItemKey } from './types';

export const PRIMARY_MENU = [
  'Messaging',
  'Content',
  'Audience',
  'Insights',
  'Integrations',
  'Support',
];

const MESSAGING_DROPDOWN: NavDropdownGroupProps = {
  header: 'Messaging',
  rbacPermission: PROJECT_PERMISSIONS.ViewMessaging,
  dropdownOptions: [
    {
      title: 'Journeys',
      href: '/workflows',
      pageDescription: 'Build personalized cross-channel user experiences',
      rbacPermission: PROJECT_PERMISSIONS.ViewMessaging,
      shouldNotReload: true,
    },
    {
      title: 'Campaigns',
      href: '/campaigns/manage',
      pageDescription: 'Create a stand alone, recurring, scheduled or triggered message',
      rbacPermission: PROJECT_PERMISSIONS.ViewMessaging,
    },
    {
      title: 'Experiments',
      href: '/experiments',
      pageDescription: 'Manage all of your campaign experiments at a glance ',
      rbacPermission: PROJECT_PERMISSIONS.ViewMessaging,
      shouldNotReload: true,
    },
  ],
};

const CONTENT_DROPDOWN: NavDropdownGroupProps = {
  header: 'Content',
  dropdownOptions: [
    {
      title: 'Templates',
      href: '/templates',
      pageDescription:
        'Manage the design of existing campaigns and create reusable base templates ',
    },
    {
      title: 'Snippets',
      href: '/templates/snippets',
      pageDescription: 'Create reusable blocks of HTML, CSS, and handlebars code',
      shouldNotReload: true,
    },
    {
      title: 'Catalogs',
      href: '/catalogs',
      pageDescription: 'Personalize your messages with data from your product catalog',
      featureFlags: ['MetadataStoreUI'],
      shouldNotReload: true,
      rbacPermission: PROJECT_PERMISSIONS.ViewCatalogs,
    },
    {
      title: 'Data Feeds',
      href: '/integrations/dataFeeds',
      pageDescription: 'Populate your messages with JSON, RSS, and XML data feeds',
      shouldNotReload: true,
      rbacPermission: PROJECT_PERMISSIONS.ManageIntegrations,
    },
    {
      title: 'Creative Library',
      href: '/creativeLibrary',
      pageDescription: 'Upload and manage images to use in your campaigns',
      shouldNotReload: true,
      featureFlags: ['AssetManagerIndex'],
    },
  ],
};

const AUDIENCE_DROPDOWN: NavDropdownGroupProps = {
  header: 'Audience',
  dropdownOptions: [
    {
      title: 'Segmentation',
      href: '/segmentation',
      pageDescription:
        'Generate audience lists that automatically update based off of changes to user and event data',
    },
    {
      title: 'Lists',
      href: '/lists',
      pageDescription: 'Manage your audience lists created via segmentation or CSV uploads',
      shouldNotReload: true,
    },
    {
      title: 'User Lookup',
      href: '/users/lookup',
      pageDescription: 'Search for contacts in your audience database and view their profiles',
      rbacPermission: PROJECT_PERMISSIONS.ViewAndExportUserData,
    },
  ],
};

const INSIGHTS_DROPDOWN: NavDropdownGroupProps = {
  header: 'Insights',
  dropdownOptions: [
    {
      title: 'Dashboard',
      href: '/dashboard',
      shouldNotReload: true,
      pageDescription:
        'Monitor key performance metrics for your campaigns with customizable widgets',
    },
    {
      title: 'Messaging Insights',
      href: '/messagingInsights',
      pageDescription:
        'See your marketing performance across campaigns and discover messaging trends',
      shouldNotReload: true,
      rbacPermission: PROJECT_PERMISSIONS.ViewReports,
    },
    {
      title: 'Intelligent Insights',
      href: '/intelligentInsights/brandAffinityInsights',
      pageDescription: 'Understand how Iterable AI is optimizing your marketing',
      rbacPermission: PROJECT_PERMISSIONS.ViewReports,
      featureFlags: ['BrandAffinity'],
    },
    {
      title: 'Goals',
      href: '/goals',
      pageDescription: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit',
      featureFlags: ['MissionControlHomepage', 'MissionControlBeta'],
    },
    {
      title: 'Predictive Goals',
      href: '/predictive-audiences',
      pageDescription: 'Create predictive metrics for your business goals and improve conversion',
      shouldNotReload: true,
      featureFlags: ['PredictiveGoals'],
    },
    {
      title: 'Reports',
      href: '/reports',
      pageDescription:
        'Customize and save campaign  performance reports over specified time frames',
      shouldNotReload: true,
      rbacPermission: PROJECT_PERMISSIONS.ViewReports,
    },
    {
      title: 'Logs',
      linksSubsection: true,
      links: [
        {
          label: 'Events',
          href: '/events',
          rbacPermission: PROJECT_PERMISSIONS.ViewAndExportUserData,
          shouldNotReload: true,
        },
        {
          label: 'Purchases',
          href: '/purchases',
          rbacPermission: PROJECT_PERMISSIONS.ViewAndExportUserData,
          shouldNotReload: true,
        },
        {
          label: 'Inbound SMS',
          href: '/users/inboundSMS',
          shouldNotReload: true,
          rbacPermission: PROJECT_PERMISSIONS.ViewAndExportUserData,
        },
        {
          label: 'Send Calendar',
          href: '/campaigns/calendar',
        },
        {
          label: 'Scheduled Messages',
          href: '/triggered/scheduledMessages',
          shouldNotReload: true,
          rbacPermission: [
            PROJECT_PERMISSIONS.ViewMessaging,
            PROJECT_PERMISSIONS.ViewAndExportUserData,
          ],
        },
        {
          label: 'Sent Messages',
          href: '/campaigns/triggeredSendLog',
          rbacPermission: [
            PROJECT_PERMISSIONS.ViewMessaging,
            PROJECT_PERMISSIONS.ViewAndExportUserData,
          ],
        },
        {
          label: 'WhatsApp Messages',
          href: '/whatsAppMessages',
          rbacPermission: [
            PROJECT_PERMISSIONS.ViewMessaging,
            PROJECT_PERMISSIONS.ViewAndExportUserData,
          ],
        },
      ],
    },
  ],
};

const INTEGRATIONS_DROPDOWN: NavDropdownGroupProps = {
  header: 'Integrations',
  rbacPermission: PROJECT_PERMISSIONS.ManageIntegrations,
  dropdownOptions: [
    {
      title: 'Third-Party',
      href: '/integrations/thirdParty',
      rbacPermission: PROJECT_PERMISSIONS.ManageIntegrations,
      shouldNotReload: true,
    },
    {
      title: 'Facebook',
      href: '/integrations/facebookIntegration',
      rbacPermission: PROJECT_PERMISSIONS.ManageIntegrations,
      shouldNotReload: true,
    },
    {
      title: 'Movable Ink',
      href: '/integrations/movableInkIntegration',
      featureFlags: ['MovableInk'],
      rbacPermission: PROJECT_PERMISSIONS.ManageIntegrations,
      shouldNotReload: true,
    },
    {
      title: 'API Keys',
      href: '/settings/apiKeys',
      rbacPermission: PROJECT_PERMISSIONS.ManageIntegrations,
      shouldNotReload: true,
    },
    {
      title: 'System Webhooks',
      href: '/integrations/webhooks',
      rbacPermission: PROJECT_PERMISSIONS.ManageIntegrations,
    },
    {
      title: 'Journey Webhooks',
      href: '/workflows/webhooks',
      rbacPermission: PROJECT_PERMISSIONS.ManageIntegrations,
    },
  ],
};
const SUPPORT_DROPDOWN: NavDropdownGroupProps = {
  header: 'Support',
  shouldOpenInNewTab: true,
  dropdownOptions: [
    {
      title: 'Knowledge base',
      href: 'https://support.iterable.com/',
    },
    {
      title: 'Submit a ticket',
      href: 'https://support.iterable.com/requests/new',
    },
    {
      title: 'API documentation',
      href: ITERABLE_API_DOCS_URL,
    },
    {
      title: 'Community',
      href: 'https://community.iterable.com',
      hideFromProspect: true,
    },
    {
      title: 'Service status',
      href: 'https://status.iterable.com/',
    },
  ],
};

export const NAVIGATION_ITEMS: Record<NavigationItemKey, NavDropdownGroupProps> = {
  MESSAGING: MESSAGING_DROPDOWN,
  CONTENT: CONTENT_DROPDOWN,
  AUDIENCE: AUDIENCE_DROPDOWN,
  INSIGHTS: INSIGHTS_DROPDOWN,
  INTEGRATIONS: INTEGRATIONS_DROPDOWN,
  SUPPORT: SUPPORT_DROPDOWN,
};
